import React, { useState } from 'react';
import styles from '../../styles/OnboardingScreen.module.css';

function QuestionScreen({ question, currentQuestionNumber, totalQuestions, initialValue, onNext, onBack }) {
  const [value, setValue] = useState(initialValue);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (value !== null && value !== undefined && value !== '' && !isSubmitting) {
      setIsSubmitting(true);
      await onNext(value);
      // Note: component may unmount before this completes
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && value !== null && value !== undefined && value !== '' && !isSubmitting) {
      handleSubmit();
    }
  };

  return (
    <>
      <div className={styles.topSection}>
        <div className={styles.questionNumber}>
          Question {currentQuestionNumber} of {totalQuestions}
        </div>
        <h2 className={styles.questionText}>{question.questionText}</h2>
        {question.subheading && (
          <p className={styles.questionSubheading}>{question.subheading}</p>
        )}
      </div>

      <div className={styles.middleSection}>
      </div>

      <div className={styles.bottomSection}>
        {question.inputType === 'slider' && (
          <SliderInput
            options={question.options}
            value={value}
            onChange={setValue}
          />
        )}

        {question.inputType === 'savings-slider' && (
          <SavingsSliderInput
            min={question.min}
            max={question.max}
            step={question.step}
            formatValue={question.formatValue}
            value={value || 0}
            onChange={setValue}
          />
        )}

        {question.inputType === 'checkbox-single' && (
          <CheckboxSingleInput
            options={question.options}
            value={value}
            onChange={setValue}
          />
        )}

        {question.inputType === 'checkbox-multiple' && (
          <CheckboxMultipleInput
            options={question.options}
            value={value}
            onChange={setValue}
            placeholder={question.placeholder}
            allowCustom={question.allowCustom}
          />
        )}

        {question.inputType === 'buttons' && (
          <ButtonsInput
            options={question.options}
            value={value}
            onChange={setValue}
          />
        )}

        {question.inputType === 'goal-cards' && (
          <GoalCardsInput
            options={question.options}
            value={value}
            onChange={setValue}
            placeholder={question.placeholder}
            allowCustom={question.allowCustom}
          />
        )}

        {question.inputType === 'selection-cards' && (
          <SelectionCardsInput
            options={question.options}
            value={value}
            onChange={setValue}
          />
        )}

        {question.inputType === 'text' && (
          <input
            type="text"
            className={styles.textInput}
            placeholder={question.placeholder}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyPress={handleKeyPress}
            autoFocus
          />
        )}

        {question.inputType === 'quick-select-with-custom' && (
          <QuickSelectWithCustomInput
            quickOptions={question.quickOptions}
            placeholder={question.placeholder}
            value={value}
            onChange={setValue}
          />
        )}

        <div className={styles.buttonGroup}>
          {onBack && (
            <button
              className={`${styles.button} ${styles.buttonSecondary}`}
              onClick={onBack}
              disabled={isSubmitting}
            >
              Back
            </button>
          )}
          <button
            className={`${styles.button} ${styles.buttonPrimary}`}
            onClick={handleSubmit}
            disabled={(value === null || value === undefined || value === '') || isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Continue'}
          </button>
        </div>
      </div>
    </>
  );
}

function SliderInput({ options, value, onChange }) {
  // Find the index of the current value, or default to middle
  const currentIndex = value
    ? options.findIndex(opt => opt.value === value)
    : Math.floor(options.length / 2);

  const [sliderIndex, setSliderIndex] = useState(
    currentIndex >= 0 ? currentIndex : Math.floor(options.length / 2)
  );

  const handleChange = (e) => {
    const index = parseInt(e.target.value, 10);
    setSliderIndex(index);
    onChange(options[index].value);
  };

  return (
    <div className={styles.sliderContainer}>
      <input
        type="range"
        min="0"
        max={options.length - 1}
        value={sliderIndex}
        onChange={handleChange}
        className={styles.slider}
      />
      <div className={styles.sliderLabel}>
        {options[sliderIndex].label}
      </div>
    </div>
  );
}

function SavingsSliderInput({ min, max, step, formatValue, value, onChange }) {
  const handleChange = (e) => {
    const newValue = parseInt(e.target.value, 10);
    onChange(newValue);
  };

  return (
    <div className={styles.sliderContainer}>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={handleChange}
        className={styles.slider}
      />
      <div className={styles.sliderLabel}>
        {formatValue ? formatValue(value) : value}
      </div>
    </div>
  );
}

function CheckboxSingleInput({ options, value, onChange }) {
  return (
    <div className={styles.checkboxContainer}>
      {options.map((option) => (
        <label
          key={option.value}
          className={`${styles.checkboxOption} ${value === option.value ? styles.checkboxOptionSelected : ''}`}
        >
          <input
            type="radio"
            name="checkbox-single"
            value={option.value}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
            className={styles.checkboxInput}
          />
          <span className={styles.checkboxLabel}>{option.label}</span>
        </label>
      ))}
    </div>
  );
}

function CheckboxMultipleInput({ options, value, onChange, placeholder, allowCustom }) {
  // Parse value (comma-separated string) into checked state
  const parseValue = (val) => {
    if (!val) return { checked: [], custom: '' };

    const parts = val.split(',').map(s => s.trim());
    const checked = [];
    const customParts = [];

    parts.forEach(part => {
      const isOption = options.some(opt => opt.value === part);
      if (isOption) {
        checked.push(part);
      } else if (part) {
        customParts.push(part);
      }
    });

    return { checked, custom: customParts.join(', ') };
  };

  const { checked: initialChecked, custom: initialCustom } = parseValue(value);
  const [checkedValues, setCheckedValues] = useState(initialChecked);
  const [customValue, setCustomValue] = useState(initialCustom);

  // Update parent value when checkboxes or custom text changes
  const updateValue = (newChecked, newCustom) => {
    const allValues = [...newChecked];
    if (newCustom && newCustom.trim()) {
      allValues.push(newCustom.trim());
    }
    onChange(allValues.join(', '));
  };

  const handleCheckboxChange = (optionValue) => {
    const newChecked = checkedValues.includes(optionValue)
      ? checkedValues.filter(v => v !== optionValue)
      : [...checkedValues, optionValue];

    setCheckedValues(newChecked);
    updateValue(newChecked, customValue);
  };

  const handleCustomChange = (e) => {
    const newCustom = e.target.value;
    setCustomValue(newCustom);
    updateValue(checkedValues, newCustom);
  };

  return (
    <div className={styles.checkboxContainer}>
      {options.map((option) => (
        <label
          key={option.value}
          className={`${styles.checkboxOption} ${checkedValues.includes(option.value) ? styles.checkboxOptionSelected : ''}`}
        >
          <input
            type="checkbox"
            value={option.value}
            checked={checkedValues.includes(option.value)}
            onChange={() => handleCheckboxChange(option.value)}
            className={styles.checkboxInput}
          />
          <span className={styles.checkboxLabel}>{option.label}</span>
        </label>
      ))}

      {allowCustom && (
        <div className={styles.customCheckboxInput}>
          <input
            type="text"
            className={styles.textInput}
            placeholder={placeholder || 'Other...'}
            value={customValue}
            onChange={handleCustomChange}
          />
        </div>
      )}
    </div>
  );
}

function ButtonsInput({ options, value, onChange }) {
  return (
    <div className={styles.buttonsContainer}>
      {options.map((option) => (
        <button
          key={option.value}
          className={`${styles.optionButton} ${value === option.value ? styles.optionButtonSelected : ''}`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function GoalCardsInput({ options, value, onChange, placeholder, allowCustom }) {
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customValue, setCustomValue] = useState('');

  // Check if the current value is one of the preset options
  const isPresetValue = options.some(opt => opt.value === value);

  // If value exists but isn't a preset, it's a custom value
  if (value && !isPresetValue && !showCustomInput) {
    setShowCustomInput(true);
    setCustomValue(value);
  }

  const handleCardClick = (optionValue) => {
    setShowCustomInput(false);
    setCustomValue('');
    onChange(optionValue);
  };

  const handleCustomClick = () => {
    setShowCustomInput(true);
    onChange('');
  };

  const handleCustomChange = (e) => {
    const newValue = e.target.value;
    setCustomValue(newValue);
    onChange(newValue);
  };

  // Construct the correct image URL based on environment
  // In production (Railway), client and server are on same domain, so /goals/ works
  // In development, need to point to server explicitly
  const getImageUrl = (filename) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    if (apiUrl) {
      // API URL is set - use it (production or custom config)
      return `${apiUrl}/goals/${filename}`;
    }
    // No API URL set - assume proxy handles it or same domain
    return `/goals/${filename}`;
  };

  return (
    <div className={styles.goalCardsContainer}>
      <div className={styles.goalCardsGrid}>
        {options.map((option) => (
          <div
            key={option.value}
            className={`${styles.goalCard} ${value === option.value ? styles.goalCardSelected : ''}`}
            onClick={() => handleCardClick(option.value)}
          >
            <div className={styles.goalCardImage}>
              <img
                src={getImageUrl(option.image)}
                alt={option.label}
                className={styles.goalCardImg}
              />
            </div>
            <div className={styles.goalCardLabel}>{option.label}</div>
          </div>
        ))}
      </div>

      {allowCustom && (
        <div className={styles.customGoalSection}>
          {!showCustomInput ? (
            <button
              className={styles.customGoalButton}
              onClick={handleCustomClick}
            >
              + Custom Goal
            </button>
          ) : (
            <input
              type="text"
              className={styles.customGoalInput}
              placeholder={placeholder}
              value={customValue}
              onChange={handleCustomChange}
              autoFocus
            />
          )}
        </div>
      )}
    </div>
  );
}

function QuickSelectWithCustomInput({ quickOptions, placeholder, value, onChange }) {
  const [showCustomInput, setShowCustomInput] = useState(false);

  // Check if current value is one of the quick options
  const isQuickOption = quickOptions.some(opt => opt.value === value);

  // If value exists but isn't a quick option, show custom input
  if (value && !isQuickOption && !showCustomInput) {
    setShowCustomInput(true);
  }

  const handleQuickSelect = (optionValue) => {
    setShowCustomInput(false);
    onChange(optionValue);
  };

  const handleCustomClick = () => {
    setShowCustomInput(true);
    onChange('');
  };

  const handleCustomChange = (e) => {
    onChange(e.target.value);
  };

  return (
    <div className={styles.quickSelectContainer}>
      <div className={styles.quickOptionsGrid}>
        {quickOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            className={`${styles.quickOptionButton} ${value === option.value ? styles.quickOptionButtonSelected : ''}`}
            onClick={() => handleQuickSelect(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className={styles.customInputSection}>
        {!showCustomInput ? (
          <button
            type="button"
            className={styles.showCustomButton}
            onClick={handleCustomClick}
          >
            {placeholder}
          </button>
        ) : (
          <input
            type="text"
            className={styles.customTextInput}
            placeholder={placeholder}
            value={value}
            onChange={handleCustomChange}
            autoFocus
          />
        )}
      </div>
    </div>
  );
}

function SelectionCardsInput({ options, value, onChange }) {
  // Construct image URL based on environment
  const getImageUrl = (filename) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    if (apiUrl) {
      return `${apiUrl}/onboarding/${filename}`;
    }
    return `/onboarding/${filename}`;
  };

  return (
    <div className={styles.selectionCardsContainer}>
      {options.map((option) => (
        <div
          key={option.value}
          className={`${styles.selectionCard} ${value === option.value ? styles.selectionCardSelected : ''} ${!option.image ? styles.selectionCardNoIcon : ''}`}
          onClick={() => onChange(option.value)}
        >
          {option.image && (
            <div className={styles.selectionCardIcon}>
              <img
                src={getImageUrl(option.image)}
                alt={option.label}
                className={styles.selectionCardImage}
              />
            </div>
          )}
          <div className={styles.selectionCardContent}>
            <div className={styles.selectionCardLabel}>{option.label}</div>
            {option.description && (
              <div className={styles.selectionCardDescription}>{option.description}</div>
            )}
          </div>
          {value === option.value && (
            <div className={styles.selectionCardCheckmark}>✓</div>
          )}
        </div>
      ))}
    </div>
  );
}

// SVG icon component for onboarding cards
// eslint-disable-next-line no-unused-vars
function OnboardingIcon({ name }) {
  const iconMap = {
    // Financial Experience icons
    'seedling': (
      <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 32V20M20 20C20 20 18 15 15 12C12 9 8 8 8 8M20 20C20 20 22 15 25 12C28 9 32 8 32 8"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="20" cy="32" r="2" fill="currentColor"/>
      </svg>
    ),
    'growing-plant': (
      <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 35V20M20 20C20 20 18 12 14 8C10 4 5 3 5 3M20 20C20 20 22 12 26 8C30 4 35 3 35 3M20 20C20 20 19 15 17 12C15 9 12 7 12 7"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <ellipse cx="20" cy="35" rx="4" ry="1.5" fill="currentColor"/>
      </svg>
    ),
    'tree': (
      <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 36V20M20 20C20 20 17 10 12 6C7 2 2 1 2 1M20 20C20 20 23 10 28 6C33 2 38 1 38 1M20 20C20 20 18 14 15 10C12 6 8 4 8 4M20 20C20 20 22 14 25 10C28 6 32 4 32 4"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <ellipse cx="20" cy="36" rx="5" ry="2" fill="currentColor"/>
      </svg>
    ),
    // Monthly Savings icons
    'empty-piggy-bank': (
      <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <ellipse cx="22" cy="20" rx="12" ry="8" stroke="currentColor" strokeWidth="2"/>
        <path d="M10 20H8C6 20 6 22 6 22V24C6 24 6 26 8 26H10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <circle cx="28" cy="18" r="1" fill="currentColor"/>
        <rect x="20" y="12" width="4" height="2" rx="1" fill="currentColor"/>
        <path d="M22 26V30M18 30H26" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    'single-coin': (
      <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="20" cy="20" r="10" stroke="currentColor" strokeWidth="2"/>
        <path d="M20 14V26M16 17L20 14L24 17M24 23L20 26L16 23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    'piggy-bank-coins': (
      <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <ellipse cx="22" cy="22" rx="11" ry="7" stroke="currentColor" strokeWidth="2"/>
        <path d="M11 22H9C7.5 22 7.5 23.5 7.5 23.5V25C7.5 25 7.5 26.5 9 26.5H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <circle cx="27" cy="20" r="1" fill="currentColor"/>
        <rect x="20" y="16" width="4" height="2" rx="1" fill="currentColor"/>
        <path d="M22 28V31M19 31H25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="12" cy="10" r="4" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M12 8V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    'fuller-piggy-bank': (
      <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <ellipse cx="22" cy="22" rx="11" ry="7" stroke="currentColor" strokeWidth="2"/>
        <path d="M11 22H9C7.5 22 7.5 23.5 7.5 23.5V25C7.5 25 7.5 26.5 9 26.5H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <circle cx="27" cy="20" r="1" fill="currentColor"/>
        <rect x="20" y="16" width="4" height="2" rx="1" fill="currentColor"/>
        <path d="M22 28V31M19 31H25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="10" cy="10" r="3.5" stroke="currentColor" strokeWidth="1.5"/>
        <circle cx="34" cy="12" r="3.5" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    ),
    'multiple-savings': (
      <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <ellipse cx="20" cy="22" rx="10" ry="6" stroke="currentColor" strokeWidth="2"/>
        <path d="M10 22H8.5C7 22 7 23.5 7 23.5V25C7 25 7 26.5 8.5 26.5H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="25" cy="20.5" r="1" fill="currentColor"/>
        <rect x="18.5" cy="17" width="3" height="1.5" rx="0.75" fill="currentColor"/>
        <path d="M20 27V29.5M17.5 29.5H22.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="10" cy="10" r="3" stroke="currentColor" strokeWidth="1.5"/>
        <circle cx="30" cy="10" r="3" stroke="currentColor" strokeWidth="1.5"/>
        <circle cx="34" cy="20" r="3" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    ),
    'treasure': (
      <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="8" y="16" width="24" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
        <path d="M8 24H32" stroke="currentColor" strokeWidth="2"/>
        <circle cx="20" cy="20" r="2" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M14 16C14 16 14 12 20 12C26 12 26 16 26 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <circle cx="13" cy="10" r="2" stroke="currentColor" strokeWidth="1.5"/>
        <circle cx="27" cy="10" r="2" stroke="currentColor" strokeWidth="1.5"/>
        <circle cx="20" cy="8" r="2" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    ),
    // Age Range icons
    'rising-sun': (
      <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="20" cy="28" r="8" stroke="currentColor" strokeWidth="2"/>
        <path d="M20 8V12M8 20H12M28 20H32M11 11L14 14M29 11L26 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <path d="M8 36H32" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    'morning-sun': (
      <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="20" cy="24" r="8" stroke="currentColor" strokeWidth="2"/>
        <path d="M20 6V10M6 20H10M30 20H34M10 10L13 13M30 10L27 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <path d="M8 34H32" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    'midday-sun': (
      <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="20" cy="20" r="8" stroke="currentColor" strokeWidth="2"/>
        <path d="M20 4V8M4 20H8M32 20H36M8 8L11 11M32 8L29 11M8 32L11 29M32 32L29 29M20 32V36" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    'afternoon-sun': (
      <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="20" cy="24" r="8" stroke="currentColor" strokeWidth="2"/>
        <path d="M20 6V10M6 20H10M30 20H34M10 30L13 27M30 30L27 27" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <path d="M8 34H32" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    'sunset': (
      <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="20" cy="30" r="6" stroke="currentColor" strokeWidth="2"/>
        <path d="M20 12V16M10 22H14M26 22H30M12 30L15 27M28 30L25 27" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <path d="M6 36H34" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
        <path d="M8 33H32" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
      </svg>
    )
  };

  return (
    <div className={styles.iconWrapper}>
      {iconMap[name] || null}
    </div>
  );
}

export default QuestionScreen;
